import { useContext, useCallback } from 'react';
import { useLocation } from '@reach/router';
import { useEnvironment, Environment } from '#core/Environment';

import TrackingContext from '../contexts/TrackingContext';
import { useInstitution } from '#core/Institution';
// JME
// import { useAuthHook } from '#utils/useAuthHook';
// JME1
import { useAuth } from '#core/Auth';

export enum EventType {
  PAGE_VIEW = 'view',
  EXIT_CLICK = 'exit.click',
  DOWNLOAD = 'download',
  APPLY = 'apply',
  SUBMIT = 'submit',
  LOGIN = 'login',
  CLICK = 'click',
  PLAY = 'play',
  PAUSE = 'pause',
  RESUME = 'resume',
  END = 'end',
  PROGRESS = 'progress',
  DISPLAY = 'display',
  CLOSE = 'close',
  ACCEPT = 'accept',
  LOGIN_FAIL = 'login-fail',
  LOGIN_SUCCESS = 'login-success',
  IMPRESSION = 'impression',
  ERROR = 'error',
}

type TrackingEventBase<ET, P> = {
  type: ET;
  payload: P & {
    id: string;
    metadata?: {
      [key: string]: any;
    };
  };
};

type LoginEvent = TrackingEventBase<EventType.LOGIN, {}>;
type LoginFailureEvent = TrackingEventBase<EventType.LOGIN_FAIL, {}>;
type LoginSuccessEvent = TrackingEventBase<EventType.LOGIN_SUCCESS, {}>;
type PageViewEvent = TrackingEventBase<EventType.PAGE_VIEW, {}>;
type ExitClickEvent = TrackingEventBase<EventType.EXIT_CLICK, {}>;
type DownloadEvent = TrackingEventBase<
  EventType.DOWNLOAD,
  {
    id: string;
    fileType: string;
  }
>;
type ApplyEvent = TrackingEventBase<EventType.APPLY, {}>;
type SubmitEvent = TrackingEventBase<EventType.SUBMIT, {}>;
type ClickEvent = TrackingEventBase<
  EventType.CLICK,
  {
    id: string;
    elementTagName: string;
  }
>;
type PlayEvent = TrackingEventBase<EventType.PLAY, {}>;
type PauseEvent = TrackingEventBase<EventType.PAUSE, {}>;
type ResumeEvent = TrackingEventBase<EventType.RESUME, {}>;
type EndEvent = TrackingEventBase<EventType.END, {}>;
type ProgressEvent = TrackingEventBase<EventType.PROGRESS, {}>;
type DisplayEvent = TrackingEventBase<EventType.DISPLAY, {}>;
type CloseEvent = TrackingEventBase<EventType.CLOSE, {}>;
type AcceptEvent = TrackingEventBase<EventType.ACCEPT, {}>;
type ImpressionEvent = TrackingEventBase<EventType.IMPRESSION, {}>;
type ErrorEvent = TrackingEventBase<
  EventType.ERROR,
  {
    type?: string;
    message?: string;
  }
>;

// Generic fallback for custom/feature-specific metadata events
// Introduced to handle the legacy Impact event tracking data
export type GenericMetadataEvent = TrackingEventBase<
  EventType,
  {
    id: string;
    metadata: Record<string, unknown>;
  }
>;

export type TrackingEvent =
  | LoginEvent
  | LoginFailureEvent
  | LoginSuccessEvent
  | PageViewEvent
  | ExitClickEvent
  | DownloadEvent
  | ApplyEvent
  | SubmitEvent
  | ClickEvent
  | PlayEvent
  | PauseEvent
  | ResumeEvent
  | EndEvent
  | ProgressEvent
  | DisplayEvent
  | CloseEvent
  | AcceptEvent
  | ImpressionEvent
  | ErrorEvent
  | GenericMetadataEvent;

type MzArguments = {
  eventType: EventType;
  payload: object;
  userMetadata: object;
  pageMetadata: {
    app: string;
    variant: string;
    type: string;
    section: string;
    subsection: string;
    pagename: string;
    id: string;
    language: string;
    environment: Environment;
    version: string;
    orgname: string;
    organisation: {
      id: string;
      name: string;
    };
  };
};

const trackMzEvent = (
  enableTracking: boolean,
  { eventType, payload, pageMetadata, userMetadata }: MzArguments,
) => {
  /* NOTE: DO NOT destructure window._mz here, MZ relies on `this` being set and destructuring breaks that
    scope. If for some reason you have to destructure, every function must either use .bind(mz) or .call(mz, ...) */
  if (!enableTracking) {
    // take over from MZ in doing this since we cant load MZ in dev without it loading google tracking etc.
    document.dispatchEvent(
      new CustomEvent('mz-event', {
        detail: {
          type: 'log',
          environment: window._mz.environment,
          event: eventType,
          data: {
            payload,
            pageMetadata,
            userMetadata,
          },
        },
      }),
    );
  } else {
    window._mz.replacePageMetadata(pageMetadata);
    window._mz.replaceUserMetadata(userMetadata);
    window._mz.emit(eventType, payload);
  }
};

export type TrackingHook = () => {
  trackEvent: (event: TrackingEvent) => void;
};

const useTracking: TrackingHook = () => {
  const trackingContext = useContext(TrackingContext);
  const { enableTracking, version, environment } = useEnvironment();
  const location = useLocation();
  const auth = useAuth();
  const { institution } = useInstitution();

  const trackEvent = useCallback(
    (event: TrackingEvent) => {
      try {
        const eventTitle =
          trackingContext.section || trackingContext.page || 'datapoints';
        trackMzEvent(enableTracking, {
          eventType: event.type,
          payload: {
            ...event.payload,
            eventTitle:
              // Legacy events have event titles set in their metadata
              event.payload.metadata?.eventTitle ||
              `${eventTitle}-${event.payload.id}`,
          },
          userMetadata: auth.isAuthenticated
            ? {
                id: auth.user?.id,
                name: auth.user?.name,
              }
            : {},
          pageMetadata: {
            app: 'datapoints',
            variant: 'datapoints',
            type: 'product',
            section: trackingContext.pageGroup || '',
            subsection: trackingContext.page || '',
            pagename: `${trackingContext.pageGroup} | ${trackingContext.page}`,
            id: `${location.pathname}.${institution.id}`,
            language: 'en',
            environment,
            version,
            orgname: institution?.name || '',
            organisation: {
              id: institution?.institutionId,
              name: institution?.name || '',
            },
          },
        });
      } catch (e) {
        // make sure the app can't be broken by errors in MZ
        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent('datapoints-error', {
              detail: (e as Error).message,
            }),
          );
        }, 0);
      }
      if (enableTracking) {
        try {
          const { newrelic } = window;
          newrelic.addPageAction(event.type, {
            ...event.payload,
            userId: auth.isAuthenticated ? auth.user?.id : '',
            pageGroup: trackingContext.pageGroup,
            page: trackingContext.page,
            version,
            organisationId: institution?.institutionId,
            organisationName: institution?.name || '',
          });
        } catch (e) {
          // make sure the app can't be broken by errors in NewRelic
          setTimeout(() => {
            document.dispatchEvent(
              new CustomEvent('datapoints-error', {
                detail: (e as Error).message,
              }),
            );
          }, 0);
        }
      }
    },
    [
      auth.isAuthenticated,
      auth.user?.id,
      auth.user?.name,
      enableTracking,
      environment,
      institution.id,
      institution?.institutionId,
      institution?.name,
      location.pathname,
      trackingContext.page,
      trackingContext.pageGroup,
      trackingContext.section,
      version,
    ],
  );

  return {
    trackEvent,
  };
};

export default useTracking;
